<template>
  <div class="ux-form" :class="formClass">
    <slot name="above" :model="formModel"> </slot>
    <slot name="fields" :fields="renderFields" :hash="fieldHash">
      <ux-field
        ref="field"
        :trail="currentTrail"
        :sandbox="sandbox"
        :submission="submission"
        :ignoreDefaults="ignoreDefaults"
        :paymentConfiguration="paymentConfiguration"
        :includeOfficeOnly="includeOfficeOnly"
        @field:mount="fieldMounted"
        @field:unmount="fieldUnmounted"
        @field:dirty="fieldDirty"
        @field:invalid="fieldInvalid"
        @field:valid="fieldValid"
        @field:error="fieldError"
        @field:focus="fieldFocus"
        @field:blur="fieldBlur"
        @field:touched="fieldTouch"
        :field="field"
        v-model="formModel"
        :parentModel="parentModel || formModel"
        :class="[fieldClass, { 'ux-field-clear': field.clear }]"
        :key="`ux-form-field-${getFieldKey(field)}-${index}`"
        v-for="(field, index) in renderFields"
      />
    </slot>
    <slot name="below" :model="formModel"> </slot>
  </div>
</template>
<script>
import UXField from "./field.vue";
import debounce from "lodash/debounce";
import _kebabCase from "lodash/kebabCase";
import { ref } from "vue";
export default {
  emits: [
    "field:unmount",
    "field:mount",
    "field:dirty",
    "field:invalid",
    "field:valid",
    "field:error",
    "field:focus",
    "field:blur",
    "field:touch",
    "form:state",
    "update:modelValue",
  ],
  props: {
    step: {
      type: Number,
    },
    parentModel: {
      type: Object,
    },
    parentForm: {
      type: Object,
    },
    trail: {
      type: Array,
      default() {
        return [];
      },
    },
    fields: {
      type: Array,
      default() {
        return [];
      },
    },
    modelValue: {
      type: Object,
      default() {
        return {};
      },
    },
    flex: {
      type: Boolean,
      default: false,
    },
    submission: {
      type: Boolean,
      default: false,
    },
    ignoreDefaults: {
      type: Boolean,
      default: false,
    },
    sandbox: {
      type: Boolean,
      default: false,
    },
    paymentConfiguration: {
      type: Object,
    },
    includeOfficeOnly: {
      type: Boolean,
      default: false,
    },
  },
  inject: {
    form: {
      default() {
        return;
      },
    },
    parentFormElement: {
      default() {
        return;
      },
    },
  },
  watch: {
    modelValue(val, old) {
      this.model = val;
    },
  },
  data() {
    return {
      mounted: false,
      model: this.modelValue,
      touched: false,
      invalid: false,
      error: false,
      dirty: false,
      focussed: false,
    };
  },
  created() {
    this.childFormElements = [];
  },
  provide() {
    const parentFormElement = this.parentFormElement || this;
    const directFormElement = this;

    return {
      parentFormElement,
      directFormElement,
    };
  },
  mounted() {
    const self = this;
    self.mounted = true;
  },
  beforeUnmount() {
    const self = this;
    self.mounted = false;
  },

  methods: {
    getCurrentFormState() {
      const self = this;
      var dirty = false;
      var invalid = false;
      var error = false;
      var touched = false;
      var focussed = false;
      var invalidFields = [];
      var fieldMap = new Map(); // To prevent duplicates based on title + base key

      function getFieldIdentifier(title, key) {
        // Extract the actual field key (last part after dots) to identify unique fields
        const baseKey = key.split(".").pop();
        return `${title}:${baseKey}`;
      }

      function collectInvalidFields(field, prefix = "") {
        // Skip fields that are not visible (hidden by expressions, office-only, etc.)
        if (!field.visible) {
          return;
        }

        // Check if this is a layout-only group (type: "group" but not asObject: true)
        const isLayoutGroup =
          field.field?.type === "group" && !field.field?.asObject;

        // Check if this is a void field (type: "void") - these are for display only
        const isVoidField = field.field?.type === "void";

        // Handle direct field validation - but prefer nested form states over direct validation
        // Only add direct field validation if there's no subform state
        if (
          field.invalid &&
          typeof field.invalid === "boolean" &&
          !field.subFormState?.invalidFields?.length
        ) {
          // Skip layout-only groups and void fields - they should never be considered invalid
          if (isLayoutGroup || isVoidField) {
            return;
          }

          const fieldPath = prefix
            ? `${prefix}.${field.field.key}`
            : field.field.key;
          const identifier = getFieldIdentifier(field.field.title, fieldPath);

          const fieldData = {
            title: field.field.title,
            key: fieldPath,
            message:
              field.validateResults?.message ||
              `Please provide a valid value for ${field.field.title}`,
          };

          // If we already have this field, keep the one with the longer/more specific path
          if (fieldMap.has(identifier)) {
            const existing = fieldMap.get(identifier);
            if (fieldPath.length > existing.key.length) {
              // Update with the more specific path
              fieldMap.set(identifier, fieldData);
              // Find and replace in the array
              const index = invalidFields.findIndex((f) => f === existing);
              if (index !== -1) {
                invalidFields[index] = fieldData;
              }
            }
          } else {
            fieldMap.set(identifier, fieldData);
            invalidFields.push(fieldData);
          }
        }

        // Handle nested form states (for groups, forms, etc.) - these take precedence
        if (
          field.subFormState?.invalidFields &&
          field.subFormState.invalidFields.length > 0
        ) {
          field.subFormState.invalidFields.forEach((nestedField) => {
            // For layout groups, don't add their key to the path
            const fieldPath = isLayoutGroup
              ? prefix
                ? `${prefix}.${nestedField.key}`
                : nestedField.key
              : prefix
                ? `${prefix}.${field.field.key}.${nestedField.key}`
                : `${field.field.key}.${nestedField.key}`;
            const identifier = getFieldIdentifier(nestedField.title, fieldPath);

            const fieldData = {
              ...nestedField,
              key: fieldPath,
            };

            // If we already have this field, keep the one with the longer/more specific path
            if (fieldMap.has(identifier)) {
              const existing = fieldMap.get(identifier);
              if (fieldPath.length > existing.key.length) {
                // Update with the more specific path
                fieldMap.set(identifier, fieldData);
                // Find and replace in the array
                const index = invalidFields.findIndex((f) => f === existing);
                if (index !== -1) {
                  invalidFields[index] = fieldData;
                }
              }
            } else {
              fieldMap.set(identifier, fieldData);
              invalidFields.push(fieldData);
            }
          });
        }
      }

      //If we are actually on the screen
      self.childFormElements.forEach(function (field) {
        if (field.dirty) {
          dirty = true;
        }

        if (field.invalid) {
          // Collect invalid fields (respects visibility)
          collectInvalidFields(field);
        }

        if (field.error) {
          error = true;
        }

        if (field.touched) {
          touched = true;
        }

        if (field.focussed) {
          focussed = true;
        }
      });

      // Set invalid based on whether we have any visible invalid fields
      invalid = invalidFields.length > 0;

      self.dirty = dirty;
      self.error = error;
      self.touched = touched;
      self.focussed = focussed;
      self.invalid = invalid;

      return {
        dirty,
        error,
        focussed,
        touched,
        invalid,
        valid: !invalid,
        invalidFields,
      };
    },
    getFieldKey(field) {
      let key = _kebabCase(`${field.title}-${field.key}`);

      if (field.incrementor) {
        key = `${key}-${field.incrementor}`;
      }
      return key;
    },
    touch() {
      (this.childFormElements || []).forEach(function (field) {
        field.touch();
      });
      this.touched = true;
    },
    untouch() {
      (this.childFormElements || []).forEach(function (field) {
        field.untouch();
      });

      this.touched = false;
    },
    reset() {
      this.untouch();

      (this.childFormElements || []).forEach(function (field) {
        field.reset();
      });
      this.model = {};
    },
    fieldUnmounted(field) {
      this.fieldStateChange(field, "unmount");
      this.$emit("field:unmount", this);
    },
    fieldMounted(field) {
      this.fieldStateChange(field, "unmount");
      this.$emit("field:mount", this);
    },
    fieldDirty(field) {
      this.fieldStateChange(field, "dirty");
      this.$emit("field:dirty", this);
    },
    fieldInvalid(field) {
      this.fieldStateChange(field, "invalid");
      this.$emit("field:invalid", this);
    },
    fieldValid(field) {
      this.fieldStateChange(field, "valid");
      this.$emit("field:valid", this);
    },
    fieldError(field) {
      this.fieldStateChange(field, "error");
      this.$emit("field:error", this);
    },
    fieldFocus(field) {
      this.fieldStateChange(field, "focus");
      this.$emit("field:focus", this);
    },
    fieldBlur(field) {
      this.fieldStateChange(field, "blur");
      this.$emit("field:blur", this);
    },
    fieldTouch(field) {
      this.fieldStateChange(field, "touch");
      this.$emit("field:touch", this);
    },
    fieldStateChange(triggerField, event) {
      var self = this;
      self.$nextTick(function () {
        self.$emit("form:state", self.getCurrentFormState());
      });
    },
  },
  components: {
    UxField: UXField,
  },
  computed: {
    currentTrail() {
      return this.trail;
    },
    fieldHash() {
      return this.renderFields.reduce(function (set, field) {
        if (!field) {
          return set;
        }
        set[field.key] = field;
        return set;
      }, {});
    },
    valid() {
      return !this.invalid;
    },
    formClass() {
      var array = [];

      if (this.flex) {
        array.push("ux-form-flex");
      }

      if (this.step || this.step === 0) {
        array.push(`ux-form-step-${this.step}`);
      }
      return array;
    },

    fieldClass() {
      var array = [];

      if (this.flex) {
        array.push("ux-field-flex");
      }

      return array;
    },
    formModel: {
      get() {
        return this.model;
      },
      set(value) {
        this.model = value;
        this.$emit("update:modelValue", this.model);
      },
    },
    renderFields() {
      return this.fields.filter(function (entry) {
        if (!entry) {
          return;
        }

        if (entry.includeIf && typeof entry.includeIf === "function") {
          return entry.includeIf(entry);
        } else if (entry.excludeIf && typeof entry.excludeIf === "function") {
          return !entry.excludeIf(entry);
        }

        return true;
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.ux-form-flex {
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  gap: 2em;

  .ux-field-flex {
    flex: 1;
    // display: flex;
    // align-items: flex-end;

    // & > .ux-field {
    // flex:1;
    // }

    &.ux-field-widget-value {
      display: none;
    }
  }

  .ux-field-clear {
    flex: none;
    width: 100%;
  }
}
</style>
